import {DateTime} from 'luxon';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {addQueryToPath, QueryStringArguments} from '../../libs/shared/helpers/src/browser/urlHelpers';
import {isElectron, isElectronVersionGTE} from '../../libs/shared/helpers/src/environment/platformHelpers';
import {findTimeZone} from '../helpers/dateTimeHelpers';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {LoadedMessageModel} from '../models/api/messageModel';
import {EmlSource, EmlSourceTypesEnum, FileEmlSource} from '../store/panels/eml/emlPanelState';

/*
 * Constants.
 */

const defaultHeight = 650;
const defaultWidth = 530;

/*
 * Popup Actions.
 */

interface OpenRelativePopupUrlOptions {
  title?: string;
  width?: number;
  height?: number;
  query?: QueryStringArguments;
}

export function openRelativePopupUrl(url: string, options: OpenRelativePopupUrlOptions = {}) {
  const {title, width, height, query} = options;

  // Build the URL.
  const fullUrl = addQueryToPath(url, query);
  const windowFeatures = getPopupWindowFeatures(width || defaultWidth, height || defaultHeight);

  // Open the window.
  return globalWindow.open(fullUrl, title || '', windowFeatures.join(','));
}

export function openExternalPopupUrl(url: string, width?: number, height?: number) {
  const windowFeatures = getPopupWindowFeatures(width || defaultWidth, height || defaultHeight);

  // Since Electron 11, opening a window with an empty URL will cause the desktop app to override it to
  // about:blank#blocked. As a workaround, we pretend to be a New Front window by passing about:blank. As a side-effect,
  // the window won't appear until Electron's ready-to-show event.
  const initialUrl = isElectron() && isElectronVersionGTE('3.27.0') ? 'about:blank' : '';
  const newWindow = globalWindow.open(initialUrl, '', windowFeatures.join(','));
  if (!newWindow) {
    return;
  }

  newWindow.opener = null;
  newWindow.location.href = url;
}

function getPopupWindowFeatures(width: number, height: number) {
  const {screenX, screenY, outerWidth, outerHeight} = globalWindow;
  const popupLeft = screenX + outerWidth / 2 - width / 2;
  const popupTop = screenY + outerHeight / 2 - height / 2;

  return [
    'menubar=no',
    'title',
    'resizable=yes',
    'scrollbars=yes',
    'location=no',
    `width=${width}`,
    `height=${height}`,
    `left=${popupLeft}`,
    `top=${popupTop}`,
  ];
}

/*
 * Conversation Popup Actions.
 */

export function openConversationPrintPopup(conversation: ConversationBaseModel) {
  const url = `/print/conversations/${conversation.id}`;
  // TODO: technically we should send the quote ids for expanded messages
  // but we do not have the functionnality yet to expand quotes yet.
  openRelativePopupUrl(url, {
    title: conversation.subject,
    query: {tz: DateTime.local().zoneName},
  });
}

/*
 * Message Popup Actions.
 */

export function openMessageRenderPopup(message: LoadedMessageModel) {
  openRelativePopupUrl(`${message.messageUrl}/render`, {title: message.subject});
}

export function openMessageSourcePopup(message: LoadedMessageModel) {
  openRelativePopupUrl(`${message.messageUrl}/source`, {title: message.subject});
}

export function openMessagePrintPopup(message: LoadedMessageModel) {
  const url = `/print/messages/${message.id}`;
  // TODO: technically we should only send the quote id if the message
  // was actually quoted but we do not have the functionnality yet.
  openRelativePopupUrl(url, {
    title: message.subject,
    query: {show_quotes: message.id, tz: findTimeZone()},
  });
}

export function openEmlPrintPopup(message: LoadedMessageModel, emlSource: EmlSource) {
  if (emlSource.type === EmlSourceTypesEnum.FILE) {
    openEmlFileContentsPrintPopup(message, emlSource);
    return;
  }

  openRelativePopupUrl(`/print/attachments/${emlSource.attachmentUid}`, {
    title: message.subject,
    query: {tz: findTimeZone()},
  });
}

function openEmlFileContentsPrintPopup(message: LoadedMessageModel, fileEmlSource: FileEmlSource) {
  const newWindow = openRelativePopupUrl('about:blank', {title: message.subject});
  if (!newWindow) {
    return;
  }

  const form = newWindow.document.createElement('form');
  const input = newWindow.document.createElement('input');
  form.appendChild(input);
  newWindow.document.body.appendChild(form);

  form.action = addQueryToPath(`${location.origin}/print/eml`, {tz: DateTime.local().zoneName});
  form.method = 'post';

  // This allows the server to accept large .eml files (vs. application/x-www-form-urlencoded)
  form.enctype = 'multipart/form-data';

  input.type = 'hidden';
  input.name = 'eml';
  input.value = fileEmlSource.emlFileContents;

  form.submit();
}
